import React, { useMemo, useState } from 'react';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { CustomizedDataGrid } from 'components/customized_data_grid';

type PaginatedResultsTableProps = {
    data: Record<string, any>[];
    columns?: any[];
    tableType: string;
    title?: string;
    rowHeight?: number;
    headerRowHeight?: number;
    isFetching?: boolean;
    isShowMoreButton?: boolean;
    pageSizeOptions?: number[];
    defaultPageSize?: number;
    onClose?: () => void;
};

const formatCellValue = (value: any) => {
    if (value === null || value === undefined || value === '') return '—';
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
};

/**
 * Shared table for EWS Rule Engine result grids (Saved Rules, Dry Run Results).
 * Owns page/pageSize state and slices `data` itself, since CustomizedDataGrid
 * only renders whatever `tableData` it is given and never paginates internally.
 */
export const PaginatedResultsTable: React.FC<PaginatedResultsTableProps> = ({
    data,
    columns,
    tableType,
    title = '',
    rowHeight = 40,
    headerRowHeight = 40,
    isFetching,
    isShowMoreButton = true,
    pageSizeOptions = [10, 25, 50, 100],
    defaultPageSize = 10,
    onClose
}) => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(String(defaultPageSize));
    const [currSorting, setCurrSorting] = useState<{ sortBy: string; sortDir: string }>({ sortBy: '', sortDir: '' });

    const rowsWithId = useMemo<Record<string, any>[]>(
        () => data.map((record, idx) => ({ id: idx + 1, ...record })),
        [data]
    );

    const sortedRows = useMemo(() => {
        const { sortBy, sortDir } = currSorting;
        if (!sortBy) return rowsWithId;
        return [...rowsWithId].sort((a, b) => {
            const aVal = a[sortBy];
            const bVal = b[sortBy];
            if (aVal === undefined || aVal === null) return 1;
            if (bVal === undefined || bVal === null) return -1;
            const aNum = Number(aVal);
            const bNum = Number(bVal);
            const result = !Number.isNaN(aNum) && !Number.isNaN(bNum)
                ? aNum - bNum
                : String(aVal).localeCompare(String(bVal));
            return sortDir === 'desc' ? -result : result;
        });
    }, [rowsWithId, currSorting]);

    const numericPageSize = Number(pageSize) || defaultPageSize;
    const totalElements = sortedRows.length;
    const totalPages = Math.max(1, Math.ceil(totalElements / numericPageSize));
    const safePage = Math.min(page, totalPages);

    const pagedRows = useMemo(() => {
        const start = (safePage - 1) * numericPageSize;
        return sortedRows.slice(start, start + numericPageSize);
    }, [sortedRows, safePage, numericPageSize]);

    const dynamicColumns = useMemo(() => {
        if (columns) return columns;
        const keys = Array.from(
            new Set(pagedRows.flatMap((row) => Object.keys(row)))
        ).filter((key) => key !== 'id');
        const keysWithData = keys.filter((key) =>
            pagedRows.some((row) => row[key] !== null && row[key] !== undefined && row[key] !== '')
        );
        return keysWithData.map((key) => ({
            name: key.replace(/_/g, ' ').toUpperCase(),
            key,
            width: 180,
            minWidth: 180,
            formatter: ({ row }: { row: Record<string, any> }) => {
                const displayValue = formatCellValue(row[key]);
                return (
                    <Tooltip title={displayValue} placement="top" arrow>
                        <Typography
                            noWrap
                            sx={{ fontSize: '0.8rem', color: '#333', px: 1.5, width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                            {displayValue}
                        </Typography>
                    </Tooltip>
                );
            }
        }));
    }, [columns, pagedRows]);

    const handlePageChange = (_event: unknown, newPage: number) => setPage(newPage);
    const handlepageSizeChange = (event: any) => {
        setPageSize(event.target.value);
        setPage(1);
    };

    const handleSortingChange = (sortingInfo: { sortBy: string; sortDir: string }) => {
        setCurrSorting(sortingInfo);
        setPage(1);
    };

    // CustomizedDataGrid's header renderer calls these unconditionally on
    // filter icon interactions this table doesn't support; safe no-ops
    // rather than left undefined.
    const noop = () => undefined;

    return (
        <>
            {onClose && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                    <Button
                        size="small"
                        onClick={onClose}
                        startIcon={<CloseIcon sx={{ fontSize: '1rem' }} />}
                        sx={{ color: '#64748b', textTransform: 'none', fontSize: '0.8rem', '&:hover': { backgroundColor: '#f1f5f9' } }}
                    >
                        Close
                    </Button>
                </Box>
            )}
            <CustomizedDataGrid
                title={title}
                tableType={tableType}
                isShowMoreButton={isShowMoreButton}
                tableColumns={dynamicColumns}
                tableData={pagedRows}
                defaultColumnNames={dynamicColumns.map((c: any) => c.name)}
                listLength={totalElements}
                rowHeight={rowHeight}
                headerRowHeight={headerRowHeight}
                page={safePage}
                pageSize={pageSize}
                paginationData={{ totalPages, totalElements }}
                handlePageChange={handlePageChange}
                handlepageSizeChange={handlepageSizeChange}
                pageSizeOptions={pageSizeOptions}
                isFetching={isFetching}
                onSorting={handleSortingChange}
                currSorting={currSorting}
                onFilterCheck={noop}
                onFilterSearch={noop}
                onHeaderSelectAll={noop}
                onFilterClick={noop}
            />
        </>
    );
};
